/**
 * The columns a split chain sheds, and the ones the trader puts back — 9.25.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HALF A SCREEN IS NOT A SMALL SCREEN, IT IS A DIFFERENT TASK
 *
 * With the positions drawer open the chain has about half a monitor, and the
 * row's twelve columns share it. Narrowing them all equally is the wrong
 * answer because they are not equally wanted: a trader who has opened the
 * drawer is managing positions and reading the ladder for the price and the
 * crowd, not fitting a volatility surface.
 *
 * So some columns LEAVE by default when the chain is split, and the space
 * goes to the ones that stay. Which ones depends on the VIEW — see `SHED`.
 * They are an opt-in rather than a hard rule, because "by default" is a
 * statement about the common case and not about what anyone is allowed to
 * see.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE TWO
 *
 * ITM%    is N(d2) from the same solve that produced the delta beside it.
 *         The two move together, and a trader reading one is not also
 *         reading the other.
 * IV      is a model output, not a market fact. It is the number you consult
 *         when choosing what to put on, which is the full-width task.
 * GAMMA   is the rate of change of the one number that stays. It matters
 *         near expiry and near the money, which is a decision to go and
 *         look rather than a column to carry on every row.
 * VEGA    prices a volatility move. On a position already on, it is read
 *         when the surface moves, not while managing a fill.
 *
 * DELTA STAYS, in both views. It is the only number on the row that says
 * what a position will DO when the underlying moves, which is the drawer's
 * own question. THETA AND RHO stay in Greeks because theta is the clock
 * running on a short book and rho is the only one left with a column.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FULL WIDTH ALWAYS SHOWS EVERYTHING
 *
 * The preference is only consulted when the chain is split. Unticking IV in
 * a split and then closing the drawer does not leave a hole in a ladder that
 * has room for it — the setting answers "what do I want in the narrow case",
 * which is the only case where anything has to go.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { ChainMode } from './chainMode'

/** A column that leaves a split chain unless it is asked back. */
export type ChainAddon = 'ITM' | 'IV' | 'GAMMA' | 'VEGA'

/**
 * What each VIEW sheds when the chain is split, in the order the menu
 * lists them.
 *
 * This table is the single authority on the question. The stylesheet knows
 * how to collapse a track and the menu knows how to draw a checkbox;
 * neither of them knows which columns a Greeks ladder can do without, and
 * that is deliberate — when the width decided it as well, the menu ended up
 * with a box ticked for a column that was not on screen.
 *
 * MARKET keeps delta and the price. GREEKS keeps delta, theta and rho
 * beside the price: four numbers wide enough to read at half a monitor,
 * and the two it drops are the ones a position is rarely managed on.
 */
const SHED: Record<ChainMode, readonly ChainAddon[]> = {
  MARKET: ['ITM', 'IV'],
  GREEKS: ['IV', 'GAMMA', 'VEGA'],
  // Combined already sheds BOTH wings wholesale below 1,200px — see the
  // container query in `terminalRedesign.css`. A second mechanism deciding
  // the same thing is how the two end up disagreeing about a column.
  COMBINED: [],
}

export const CHAIN_ADDONS = ['ITM', 'IV', 'GAMMA', 'VEGA'] as const

/** Where the opt-ins are remembered, per browser. */
export const CHAIN_ADDONS_KEY = 'apna.chainAddons'

export const CHAIN_ADDON_LABEL: Record<ChainAddon, string> = {
  ITM: 'ITM chance',
  IV: 'Implied volatility',
  GAMMA: 'Gamma',
  VEGA: 'Vega',
}

export const CHAIN_ADDON_HINT: Record<ChainAddon, string> = {
  ITM: 'The model chance of finishing in the money, per side',
  IV: 'Implied volatility in vol points, per side',
  GAMMA: 'Delta change per point of the underlying',
  VEGA: 'Value change per one percentage-point of IV',
}

/** The class that collapses each one's track. */
const SHED_CLASS: Record<ChainAddon, string> = {
  ITM: 'tr-no-itm',
  IV: 'tr-no-iv',
  GAMMA: 'tr-no-gam',
  VEGA: 'tr-no-veg',
}

const isAddon = (v: unknown): v is ChainAddon =>
  typeof v === 'string' && (CHAIN_ADDONS as readonly string[]).includes(v)

/**
 * Read stored opt-ins back.
 *
 * Validated rather than cast, for the reason `parseChainMode` is: this
 * comes out of `localStorage`, which is external input like any other, and
 * an older build may have left a value this one does not know. Anything
 * unrecognised is dropped rather than failing the whole read — a stale
 * entry should cost its own column, not the setting.
 */
export const parseChainAddons = (v: unknown): ChainAddon[] =>
  Array.isArray(v) ? v.filter(isAddon) : []

/**
 * Which add-ons this MODE can offer.
 *
 * Asked of the mode rather than hard-coded at the call site because the
 * three disagree, and a checkbox that controls a column the current view
 * does not draw is a control that does nothing — which reads as a bug.
 */
export function addonsFor(mode: ChainMode): readonly ChainAddon[] {
  return SHED[mode]
}

/**
 * The columns to LEAVE OUT, given the view, the split and what was asked
 * back.
 *
 * Empty at full width, whatever is ticked: the preference answers "what do
 * I want in the narrow case", and unticking IV in a split should not leave
 * a hole in a ladder that has room for it.
 */
export function shedColumns({
  mode,
  split,
  addons,
}: {
  mode: ChainMode
  split: boolean
  addons: readonly ChainAddon[]
}): ChainAddon[] {
  if (!split) return []
  return SHED[mode].filter((a) => !addons.includes(a))
}

/**
 * The classes the table wears so the stylesheet can collapse the tracks.
 *
 * A string for the same reason `chainModeClass` is one: it is concatenated
 * onto `tr-tbl` at one call site, and a caller assembling class names from
 * the shed list would be the second place that knows the mapping.
 */
export const chainColumnsClass = (shed: readonly ChainAddon[]): string =>
  shed.map((a) => ` ${SHED_CLASS[a]}`).join('')
